import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { 
  CalendarDays, Plus, ChevronLeft, ChevronRight, Clock, MapPin, User, 
  Stethoscope, Check, ChevronsUpDown, AlertCircle, CheckCircle2, XCircle,
  RefreshCw, Filter, Search, Building, FileText, CalendarClock, ExternalLink,
  ChevronDown, ChevronUp, AlertTriangle
} from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfDay, addDays, subDays, addWeeks, subWeeks, isSameDay, parseISO, isBefore, addMinutes } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

import { API } from "@/config/api";
import { AREAS, REP_DIRECTORY, getRepDisplayName } from "@/config/constants";


const VISIT_TYPES = ["Initial visit", "Spec call", "Detail call", "Detail Appointment", "Drop Off", "Lunch", "Booking"];
const STATUSES = ["Planned", "Completed", "No Show", "Rescheduled"];

// Time grid configuration - Extended hours 05:00-22:00
const GRID_START_HOUR = 5;   // 05:00
const GRID_END_HOUR = 22;    // 22:00
const SLOT_MINUTES = 30;     // 30-min slots
const SLOT_HEIGHT = 48;      // pixels per slot

// Generate time slots for the grid
const TIME_SLOTS = [];
for (let hour = GRID_START_HOUR; hour < GRID_END_HOUR; hour++) {
  TIME_SLOTS.push(`${hour.toString().padStart(2, '0')}:00`);
  TIME_SLOTS.push(`${hour.toString().padStart(2, '0')}:30`);
}

// No Show reason options
const NO_SHOW_REASONS = [
  "Doctor unavailable",
  "Ran out of time",
  "Practice closed",
  "Wrong address",
  "Cancelled last minute",
  "Weather conditions",
  "Other"
];

// Reschedule reason options
const RESCHEDULE_REASONS = [
  "Doctor requested different time",
  "Rep scheduling conflict",
  "Emergency",
  "Travel issues",
  "Practice request",
  "Other"
];

// Status colors and icons
const STATUS_CONFIG = {
  "Planned": { color: "bg-blue-100 text-blue-800 border-blue-300", bgColor: "bg-blue-50", timedBg: "bg-blue-500", untimedBg: "bg-blue-200", icon: CalendarClock },
  "Completed": { color: "bg-green-100 text-green-800 border-green-300", bgColor: "bg-green-50", timedBg: "bg-green-500", untimedBg: "bg-green-200", icon: CheckCircle2 },
  "No Show": { color: "bg-red-100 text-red-800 border-red-300", bgColor: "bg-red-50", timedBg: "bg-red-500", untimedBg: "bg-red-200", icon: XCircle },
  "Rescheduled": { color: "bg-orange-100 text-orange-800 border-orange-300", bgColor: "bg-orange-50", timedBg: "bg-orange-500", untimedBg: "bg-orange-200", icon: RefreshCw }
};

// Helper: Parse time string to minutes from midnight
const timeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const [hours, mins] = timeStr.split(':').map(Number);
  return hours * 60 + mins;
};

// Helper: Get slot index from time
const getSlotIndex = (timeStr) => {
  const minutes = timeToMinutes(timeStr);
  if (minutes === null) return -1;
  const startMinutes = GRID_START_HOUR * 60;
  return Math.floor((minutes - startMinutes) / SLOT_MINUTES);
};

// Helper: Format time for display
const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${ampm}`;
};

// Helper: Format time range (e.g., "09:00–09:30")
const formatTimeRange = (startTime, endTime) => {
  if (!startTime) return 'No time';
  const start = startTime.substring(0, 5);
  if (endTime) {
    return `${start}–${endTime.substring(0, 5)}`;
  }
  return start;
};

// Helper: Truncate address for preview (35-50 chars)
const truncateAddress = (address, maxLength = 40) => {
  if (!address) return '';
  if (address.length <= maxLength) return address;
  return address.substring(0, maxLength).trim() + '...';
};

const RepCalendarPage = ({ user: propUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(propUser || null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("week"); // day or week
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Fetch current user if not provided via prop
  useEffect(() => {
    if (propUser) {
      setUser(propUser);
      return;
    }
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error("Failed to fetch user");
      }
    };
    fetchUser();
  }, [propUser]);
  
  // Filters
  const [filterArea, setFilterArea] = useState("");
  const [filterRep, setFilterRep] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDoctorId, setFilterDoctorId] = useState("");
  const [filterDoctor, setFilterDoctor] = useState(null);
  
  // Plan/Edit Appointment Dialog
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null); // null = create mode, object = edit mode
  const [planForm, setPlanForm] = useState({
    doctor_id: "",
    doctor_name: "",
    practice_name: "",
    area: "",
    appointment_date: format(new Date(), "yyyy-MM-dd"),
    appointment_time: "",
    end_time: "",
    visit_type: "Follow-up",
    notes: "",
    assigned_rep: "",
    is_new_doctor: false,
    // New doctor draft fields
    new_doctor_phone: "",
    new_doctor_email: "",
    // Doctor snapshot fields for calendar display
    doctor_address_snapshot: "",
    doctor_phone_snapshot: ""
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Conflict check state
  const [conflictWarning, setConflictWarning] = useState(null);
  const [conflictChecking, setConflictChecking] = useState(false);
  
  const checkAppointmentConflicts = async (date, time, checkForRepName = null) => {
    if (!date || !time) return;
    let repName = checkForRepName;
    if (!repName) repName = user?.assigned_rep_name || user?.name;
    if (!repName) return;
    setConflictChecking(true);
    setConflictWarning(null);
    try {
      const response = await axios.post(`${API}/calendar/appointments/check-conflicts`, {
        rep_name: repName, appointment_date: date, appointment_time: time, duration_minutes: 60
      });
      setConflictWarning(response.data.has_conflicts ? response.data.conflicts : null);
    } catch { setConflictWarning(null); } finally { setConflictChecking(false); }
  };
  
  // Unscheduled section expanded state
  const [unscheduledExpanded, setUnscheduledExpanded] = useState(true);
  
  // Doctor search for plan form
  const [doctorSearchOpen, setDoctorSearchOpen] = useState(false);
  const [doctorSearchQuery, setDoctorSearchQuery] = useState("");
  const [doctorSearchResults, setDoctorSearchResults] = useState([]);
  const [searchingDoctors, setSearchingDoctors] = useState(false);
  const [selectedPlanDoctor, setSelectedPlanDoctor] = useState(null);
  
  // Doctor search for filter
  const [filterDoctorSearchOpen, setFilterDoctorSearchOpen] = useState(false);
  const [filterDoctorSearchQuery, setFilterDoctorSearchQuery] = useState("");
  const [filterDoctorSearchResults, setFilterDoctorSearchResults] = useState([]);
  
  // Appointment Detail Sheet
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Reschedule Panel (inline in sheet)
  const [showReschedulePanel, setShowReschedulePanel] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [rescheduleOtherReason, setRescheduleOtherReason] = useState("");
  
  // No Show modal state
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [noShowReason, setNoShowReason] = useState("");
  const [noShowOtherReason, setNoShowOtherReason] = useState("");
  
  // Unscheduled popover state for "+X more" expansion
  const [unscheduledPopoverDay, setUnscheduledPopoverDay] = useState(null);
  const [unscheduledPopoverOpen, setUnscheduledPopoverOpen] = useState(false);
  
  // Log Visit Prompt
  const [logVisitPromptOpen, setLogVisitPromptOpen] = useState(false);
  const [completedAppointment, setCompletedAppointment] = useState(null);
  
  // Conflict Detection State
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictingAppointments, setConflictingAppointments] = useState([]);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  const isManagerOrAdmin = user?.role === "manager" || user?.role === "admin";

  // Handle return from DoctorsPage with new doctor created
  useEffect(() => {
    const newDoctorCreated = searchParams.get("new_doctor_created");
    const doctorId = searchParams.get("doctor_id");
    const doctorName = searchParams.get("doctor_name");
    const practiceName = searchParams.get("practice_name");
    const area = searchParams.get("area");
    
    if (newDoctorCreated === "true" && doctorId) {
      // Clear the URL params
      setSearchParams({});
      
      // Pre-select the new doctor in the plan form and open dialog
      const newDoctor = {
        doctor_id: doctorId,
        doctor_name: doctorName || "",
        practice_name: practiceName || "",
        area: area || ""
      };
      
      setSelectedPlanDoctor(newDoctor);
      setPlanForm(prev => ({
        ...prev,
        doctor_id: doctorId,
        doctor_name: doctorName || "",
        practice_name: practiceName || "",
        area: area || "",
        appointment_date: format(new Date(), "yyyy-MM-dd")
      }));
      
      toast.success("Doctor added! Now complete your appointment details.", {
        duration: 4000
      });
      
      // Open the plan dialog with new doctor pre-selected
      setPlanDialogOpen(true);
    }
  }, [searchParams, setSearchParams]);

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    if (!user) return; // Wait for user to load
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Date range based on view
      if (viewMode === "day") {
        params.append("start_date", format(currentDate, "yyyy-MM-dd"));
        params.append("end_date", format(currentDate, "yyyy-MM-dd"));
      } else {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        params.append("start_date", format(weekStart, "yyyy-MM-dd"));
        params.append("end_date", format(weekEnd, "yyyy-MM-dd"));
      }
      
      if (filterArea) params.append("area", filterArea);
      if (filterRep && isManagerOrAdmin) params.append("rep", filterRep);
      if (filterStatus) params.append("status", filterStatus);
      if (filterDoctorId) params.append("doctor_id", filterDoctorId);
      
      const response = await axios.get(`${API}/calendar/appointments?${params.toString()}`);
      setAppointments(response.data);
    } catch (error) {
      toast.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode, filterArea, filterRep, filterStatus, filterDoctorId, isManagerOrAdmin, user]);

  useEffect(() => {
    if (user) {
      fetchAppointments();
    }
  }, [fetchAppointments, user]);

  // Search doctors for plan form
  const searchDoctors = useCallback(async (query, setResults) => {
    try {
      setSearchingDoctors(true);
      const url = query.length >= 2 
        ? `${API}/doctors/search?q=${encodeURIComponent(query)}&limit=15`
        : `${API}/doctors/search?limit=10`;
      const response = await axios.get(url);
      setResults(response.data);
    } catch (error) {
      console.error("Failed to search doctors");
    } finally {
      setSearchingDoctors(false);
    }
  }, []);

  useEffect(() => {
    if (!doctorSearchOpen) return;
    const timer = setTimeout(() => searchDoctors(doctorSearchQuery, setDoctorSearchResults), 300);
    return () => clearTimeout(timer);
  }, [doctorSearchQuery, doctorSearchOpen, searchDoctors]);

  useEffect(() => {
    if (!filterDoctorSearchOpen) return;
    const timer = setTimeout(() => searchDoctors(filterDoctorSearchQuery, setFilterDoctorSearchResults), 300);
    return () => clearTimeout(timer);
  }, [filterDoctorSearchQuery, filterDoctorSearchOpen, searchDoctors]);

  // Handle doctor selection for plan form
  const handlePlanDoctorSelect = (doctor) => {
    setSelectedPlanDoctor(doctor);
    setPlanForm(prev => ({
      ...prev,
      doctor_id: doctor.doctor_id,
      doctor_name: doctor.doctor_name,
      practice_name: doctor.practice_name || "",
      area: doctor.area || doctor.city_territory || "",
      // Store snapshot data from doctor record
      doctor_address_snapshot: doctor.address || "",
      doctor_phone_snapshot: doctor.doctor_phone || ""
    }));
    setDoctorSearchOpen(false);
    setDoctorSearchQuery("");
  };

  // Handle filter doctor selection
  const handleFilterDoctorSelect = (doctor) => {
    setFilterDoctor(doctor);
    setFilterDoctorId(doctor.doctor_id);
    setFilterDoctorSearchOpen(false);
    setFilterDoctorSearchQuery("");
  };

  // Create appointment (existing doctors only)
  const handleCreateAppointment = async (forceDoubleBook = false) => {
    if (!planForm.assigned_rep) {
      toast.error("Please select a rep");
      return;
    }
    if (!planForm.doctor_id) {
      toast.error("Please select a doctor");
      return;
    }
    if (!planForm.appointment_date) {
      toast.error("Please select a date");
      return;
    }
    if (!planForm.area) {
      toast.error("Please select an area");
      return;
    }

    // Build payload
    const payload = {
      doctor_id: planForm.doctor_id,
      doctor_name: planForm.doctor_name,
      practice_name: planForm.practice_name,
      area: planForm.area,
      appointment_date: planForm.appointment_date,
      appointment_time: planForm.appointment_time || null,
      end_time: planForm.end_time || null,
      visit_type: planForm.visit_type,
      notes: planForm.notes,
      assigned_rep: planForm.assigned_rep,
      is_new_doctor: false,
      new_doctor_draft: null,
      doctor_address_snapshot: planForm.doctor_address_snapshot || null,
      doctor_phone_snapshot: planForm.doctor_phone_snapshot || null,
      allow_double_book: forceDoubleBook
    };

    // If not forcing double-book and there's a time set, check for conflicts
    if (!forceDoubleBook && planForm.appointment_time) {
      setCheckingConflicts(true);
      try {
        const conflictCheck = await axios.post(`${API}/calendar/appointments/check-conflicts`, payload);
        
        if (conflictCheck.data.has_conflicts) {
          // Show conflict dialog
          setConflictingAppointments(conflictCheck.data.conflicts);
          setPendingAppointmentData(payload);
          setConflictDialogOpen(true);
          setCheckingConflicts(false);
          return;
        }
      } catch (error) {
        console.error("Conflict check failed:", error);
        // Continue with save if conflict check fails
      } finally {
        setCheckingConflicts(false);
      }
    }

    // No conflicts or force double-book - proceed with save
    setSubmitting(true);
    try {
      await axios.post(`${API}/calendar/appointments`, payload);
      toast.success("Appointment planned successfully");
      setPlanDialogOpen(false);
      resetPlanForm();
      fetchAppointments();
    } catch (error) {
      console.error('[APPOINTMENT] Create error:', error?.message);
      toast.error(error.response?.data?.detail || "Failed to create appointment");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Confirm double-booking after user acknowledges conflict
  const handleConfirmDoubleBook = async () => {
    if (!pendingAppointmentData) return;
    
    setSubmitting(true);
    try {
      const payload = {
        ...pendingAppointmentData,
        allow_double_book: true,
        conflict_group_id: conflictingAppointments[0]?.appointment_id || null // Link to first conflict
      };
      
      await axios.post(`${API}/calendar/appointments`, payload);
      toast.success("Appointment created (double-booking confirmed)");
      setConflictDialogOpen(false);
      setConflictingAppointments([]);
      setPendingAppointmentData(null);
      setPlanDialogOpen(false);
      resetPlanForm();
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appointment");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Cancel double-booking - close dialog, let user change time
  const handleCancelDoubleBook = () => {
    setConflictDialogOpen(false);
    setConflictingAppointments([]);
    setPendingAppointmentData(null);
    toast.info("Please select a different time slot");
  };

  // Update existing appointment
  const handleUpdateAppointment = async (forceDoubleBook = false) => {
    if (!editingAppointment) return;
    
    const payload = {
      doctor_id: planForm.doctor_id,
      doctor_name: planForm.doctor_name,
      practice_name: planForm.practice_name,
      area: planForm.area,
      appointment_date: planForm.appointment_date,
      appointment_time: planForm.appointment_time || null,
      end_time: planForm.end_time || null,
      visit_type: planForm.visit_type,
      notes: planForm.notes,
      assigned_rep: planForm.assigned_rep,
      doctor_address_snapshot: planForm.doctor_address_snapshot || null,
      doctor_phone_snapshot: planForm.doctor_phone_snapshot || null,
      allow_double_book: forceDoubleBook
    };
    
    // Check for conflicts if time changed and not forcing double-book
    const timeChanged = planForm.appointment_time !== editingAppointment.appointment_time ||
                       planForm.appointment_date !== editingAppointment.appointment_date;
    
    if (!forceDoubleBook && planForm.appointment_time && timeChanged) {
      setCheckingConflicts(true);
      try {
        const conflictCheck = await axios.post(
          `${API}/calendar/appointments/check-conflicts?exclude_id=${editingAppointment.appointment_id}`,
          payload
        );
        
        if (conflictCheck.data.has_conflicts) {
          setConflictingAppointments(conflictCheck.data.conflicts);
          setPendingAppointmentData({ ...payload, isUpdate: true, appointmentId: editingAppointment.appointment_id });
          setConflictDialogOpen(true);
          setCheckingConflicts(false);
          return;
        }
      } catch (error) {
        console.error("Conflict check failed:", error);
      } finally {
        setCheckingConflicts(false);
      }
    }
    
    setSubmitting(true);
    try {
      await axios.patch(`${API}/calendar/appointments/${editingAppointment.appointment_id}`, payload);
      toast.success("Appointment updated successfully");
      setPlanDialogOpen(false);
      resetPlanForm();
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Confirm double-booking for updates
  const handleConfirmDoubleBookUpdate = async () => {
    if (!pendingAppointmentData || !pendingAppointmentData.isUpdate) return;
    
    setSubmitting(true);
    try {
      const payload = {
        ...pendingAppointmentData,
        allow_double_book: true
      };
      delete payload.isUpdate;
      delete payload.appointmentId;
      
      await axios.patch(`${API}/calendar/appointments/${pendingAppointmentData.appointmentId}`, payload);
      toast.success("Appointment updated (double-booking confirmed)");
      setConflictDialogOpen(false);
      setConflictingAppointments([]);
      setPendingAppointmentData(null);
      setPlanDialogOpen(false);
      resetPlanForm();
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handler for "New Doctor" button - redirects to Doctors page with return URL
  const handleNewDoctorRedirect = () => {
    setPlanDialogOpen(false);
    resetPlanForm();
    
    // Build return URL with context so DoctorsPage can redirect back after creation
    const returnUrl = `/rep-calendar?new_doctor_created=true`;
    navigate(`/doctors?returnTo=${encodeURIComponent(returnUrl)}&context=calendar`);
  };

  // Update appointment status (for simple status changes like Completed)
  const handleUpdateStatus = async (appointmentId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/calendar/appointments/${appointmentId}`, {
        status: newStatus
      });
      toast.success(`Appointment marked as ${newStatus}`);
      
      // If completed, prompt to log visit
      if (newStatus === "Completed") {
        setCompletedAppointment(selectedAppointment);
        setLogVisitPromptOpen(true);
      }
      
      setDetailSheetOpen(false);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // Handle No Show with required reason
  const handleNoShowWithReason = async () => {
    if (!selectedAppointment) return;
    
    const finalReason = noShowReason === "Other" 
      ? noShowOtherReason.trim() 
      : noShowReason;
    
    if (!finalReason || finalReason.length < 3) {
      toast.error("Please provide a reason for marking as No Show");
      return;
    }
    
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/calendar/appointments/${selectedAppointment.appointment_id}`, {
        status: "No Show",
        no_show_reason: finalReason
      });
      toast.success("Appointment marked as No Show");
      setShowNoShowModal(false);
      setNoShowReason("");
      setNoShowOtherReason("");
      setDetailSheetOpen(false);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as No Show");
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // Handle Reschedule with required reason + date
  const handleRescheduleWithReason = async () => {
    if (!selectedAppointment) return;
    
    const finalReason = rescheduleReason === "Other"
      ? rescheduleOtherReason.trim()
      : rescheduleReason;
    
    if (!rescheduleDate) {
      toast.error("Please select a new date");
      return;
    }
    if (!finalReason || finalReason.length < 3) {
      toast.error("Please provide a reason for rescheduling");
      return;
    }
    
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/calendar/appointments/${selectedAppointment.appointment_id}`, {
        status: "Rescheduled",
        reschedule_date: rescheduleDate,
        reschedule_reason: finalReason
      });
      toast.success("Appointment rescheduled successfully");
      setShowReschedulePanel(false);
      setRescheduleDate("");
      setRescheduleReason("");
      setRescheduleOtherReason("");
      setDetailSheetOpen(false);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule appointment");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Reschedule appointment with reason (legacy handler)
  const handleReschedule = async (appointmentId, newDate, reason) => {
    if (!newDate) {
      toast.error("Please select a new date");
      return;
    }
    if (!reason || reason.trim().length < 3) {
      toast.error("Please provide a reason for rescheduling");
      return;
    }
    
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/calendar/appointments/${appointmentId}`, {
        reschedule_date: newDate,
        reschedule_reason: reason
      });
      toast.success("Appointment rescheduled");
      setDetailSheetOpen(false);
      setShowReschedulePanel(false);
      setRescheduleDialogOpen(false);
      setRescheduleDate("");
      setRescheduleReason("");
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      doctor_id: "",
      doctor_name: "",
      practice_name: "",
      area: "",
      appointment_date: format(new Date(), "yyyy-MM-dd"),
      appointment_time: "",
      end_time: "",
      visit_type: "Follow-up",
      notes: "",
      assigned_rep: user?.role === "rep" ? user?.assigned_rep_name || "" : "",
      is_new_doctor: false,
      new_doctor_phone: "",
      new_doctor_email: "",
      doctor_address_snapshot: "",
      doctor_phone_snapshot: ""
    });
    setSelectedPlanDoctor(null);
    setEditingAppointment(null);
    setConflictWarning(null);
    setConflictChecking(false);
  };

  // Open dialog for creating appointment at specific date/time slot
  const handleSlotClick = (date, time = null) => {
    resetPlanForm();
    setPlanForm(prev => ({
      ...prev,
      appointment_date: format(date, "yyyy-MM-dd"),
      appointment_time: time || "",
      end_time: time ? format(addMinutes(parseISO(`2000-01-01T${time}`), 30), "HH:mm") : "",
      assigned_rep: user?.role === "rep" ? user?.assigned_rep_name || "" : ""
    }));
    setEditingAppointment(null);
    setPlanDialogOpen(true);
  };

  // Open dialog for editing existing appointment
  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setPlanForm({
      doctor_id: appointment.doctor_id || "",
      doctor_name: appointment.doctor_name || "",
      practice_name: appointment.practice_name || "",
      area: appointment.area || "",
      appointment_date: appointment.appointment_date || "",
      appointment_time: appointment.appointment_time || "",
      end_time: appointment.end_time || "",
      visit_type: appointment.visit_type || "Follow-up",
      notes: appointment.notes || "",
      assigned_rep: appointment.rep_name || "",
      is_new_doctor: false,
      new_doctor_phone: "",
      new_doctor_email: "",
      doctor_address_snapshot: appointment.doctor_address_snapshot || "",
      doctor_phone_snapshot: appointment.doctor_phone_snapshot || ""
    });
    if (appointment.doctor_id) {
      setSelectedPlanDoctor({
        doctor_id: appointment.doctor_id,
        doctor_name: appointment.doctor_name,
        practice_name: appointment.practice_name,
        address: appointment.doctor_address_snapshot || "",
        doctor_phone: appointment.doctor_phone_snapshot || ""
      });
    }
    setDetailSheetOpen(false);
    setPlanDialogOpen(true);
  };

  // Navigation
  const goToToday = () => setCurrentDate(new Date());
  const goPrevious = () => {
    if (viewMode === "day") {
      setCurrentDate(subDays(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };
  const goNext = () => {
    if (viewMode === "day") {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = useCallback((date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return appointments.filter(apt => apt.appointment_date === dateStr);
  }, [appointments]);

  // Separate timed and untimed appointments for a date
  const getAppointmentsByType = useCallback((date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const dayAppointments = appointments.filter(apt => apt.appointment_date === dateStr);
    
    const timed = dayAppointments.filter(apt => apt.appointment_time);
    const untimed = dayAppointments.filter(apt => !apt.appointment_time);
    
    return { timed, untimed };
  }, [appointments]);

  // Check if appointment has a time set
  const hasTimes = (appointment) => !!appointment.appointment_time;

  // Check if appointment is overdue
  const isOverdue = (appointment) => {
    if (appointment.status !== "Planned") return false;
    const today = startOfDay(new Date());
    const aptDate = parseISO(appointment.appointment_date);
    return isBefore(aptDate, today);
  };

  // Generate week days
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  // Clear filters
  const clearFilters = () => {
    setFilterArea("");
    setFilterRep("");
    setFilterStatus("");
    setFilterDoctorId("");
    setFilterDoctor(null);
  };

  const hasFilters = filterArea || filterRep || filterStatus || filterDoctorId;

  // Render appointment card with visual priority (timed vs untimed)
  const renderAppointmentCard = (appointment, compact = false, isTimedView = false) => {
    const StatusIcon = STATUS_CONFIG[appointment.status]?.icon || CalendarClock;
    const overdue = isOverdue(appointment);
    const hasTimes = !!appointment.appointment_time;
    
    // Visual priority: timed appointments are more prominent
    const timedBg = STATUS_CONFIG[appointment.status]?.timedBg || "bg-blue-500";
    const untimedBg = STATUS_CONFIG[appointment.status]?.untimedBg || "bg-blue-200";
    
    // Build event summary line: [Visit Type] • [Time Range] • [Address Preview]
    const visitType = appointment.visit_type || "Visit";
    const timeRange = formatTimeRange(appointment.appointment_time, appointment.end_time);
    const addressPreview = truncateAddress(appointment.doctor_address_snapshot || appointment.area || "", 35);
    
    return (
      <motion.div
        key={appointment.appointment_id}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "rounded-lg cursor-pointer transition-all hover:shadow-md overflow-hidden",
          isTimedView ? "h-full" : "p-2 border",
          hasTimes && isTimedView 
            ? `${timedBg} text-white shadow-sm` 
            : hasTimes 
              ? `${timedBg} text-white` 
              : `${untimedBg} text-gray-800 border-dashed`,
          overdue && "ring-2 ring-red-400 ring-offset-1"
        )}
        onClick={() => {
          setSelectedAppointment(appointment);
          setDetailSheetOpen(true);
        }}
        data-testid={`appointment-${appointment.appointment_id}`}
        title={`${visitType} · ${appointment.doctor_name} · ${appointment.area || "Area unknown"}${timeRange ? ` · ${timeRange}` : ""}`}
      >
        <div className={cn("flex items-start gap-2", isTimedView ? "p-1.5 h-full" : "justify-between")}>
          <div className="flex-1 min-w-0">
            {/* Line 1: Doctor Name */}
            <p className={cn(
              "font-medium truncate",
              compact ? "text-xs" : "text-sm",
              hasTimes && !isTimedView ? "text-white" : ""
            )}>
              {appointment.doctor_name}
            </p>
            {/* Doctor area — always visible, not just on hover */}
            <p
              className={cn(
                "text-[10px] truncate flex items-center gap-0.5",
                hasTimes ? "text-white/90" : "text-muted-foreground"
              )}
              data-testid={`appointment-area-${appointment.appointment_id}`}
            >
              <MapPin className="w-2.5 h-2.5 shrink-0" />
              {appointment.area || "Area unknown"}
            </p>
            {/* Line 2: Visit Type • Time • Address Preview */}
            <p className={cn(
              "text-xs truncate mt-0.5",
              hasTimes ? "text-white/80" : "text-muted-foreground"
            )}>
              {compact ? (
                // Compact: just show time
                timeRange
              ) : (
                // Full: Visit Type • Time • Address
                <>
                  <span className="font-medium">{visitType}</span>
                  <span className="mx-1">•</span>
                  <span>{timeRange}</span>
                  {addressPreview && (
                    <>
                      <span className="mx-1">•</span>
                      <span className="opacity-80">{addressPreview}</span>
                    </>
                  )}
                </>
              )}
            </p>
          </div>
          {!isTimedView && !compact && (
            <Badge className={cn(
              "text-[10px] h-5 shrink-0",
              hasTimes ? "bg-white/20 text-white border-0" : STATUS_CONFIG[appointment.status]?.color
            )}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {appointment.status}
            </Badge>
          )}
        </div>
        {/* Show No Show / Reschedule reason */}
        {appointment.status === "No Show" && appointment.no_show_reason && !compact && (
          <p className={cn(
            "text-[10px] truncate mt-1",
            hasTimes ? "text-white/80" : "text-red-600"
          )} title={appointment.no_show_reason}>
            ❌ {truncateAddress(appointment.no_show_reason, 30)}
          </p>
        )}
        {appointment.status === "Rescheduled" && appointment.reschedule_history?.length > 0 && !compact && (
          <p className={cn(
            "text-[10px] truncate mt-1",
            hasTimes ? "text-white/80" : "text-orange-600"
          )} title={appointment.reschedule_history[appointment.reschedule_history.length - 1]?.reason}>
            🔁 {truncateAddress(appointment.reschedule_history[appointment.reschedule_history.length - 1]?.reason || "", 30)}
          </p>
        )}
        {overdue && !isTimedView && (
          <div className={cn(
            "flex items-center gap-1 mt-1",
            hasTimes ? "text-white" : "text-red-600"
          )}>
            <AlertCircle className="w-3 h-3" />
            <span className="text-[10px] font-medium">Overdue</span>
          </div>
        )}
      </motion.div>
    );
  };

  // Render time grid event (positioned by time)
  // Calculate overlapping appointments and their positions
  const getOverlappingGroups = useCallback((timedAppointments) => {
    if (timedAppointments.length === 0) return [];
    
    // Sort by start time
    const sorted = [...timedAppointments].sort((a, b) => {
      return (a.appointment_time || "").localeCompare(b.appointment_time || "");
    });
    
    // Find overlapping groups
    const groups = [];
    let currentGroup = [];
    let currentGroupEnd = null;
    
    for (const apt of sorted) {
      const aptStart = apt.appointment_time;
      const aptEnd = apt.end_time || (aptStart ? addMinutesToTime(aptStart, 30) : null);
      
      if (!aptStart) continue;
      
      if (currentGroup.length === 0) {
        currentGroup.push(apt);
        currentGroupEnd = aptEnd;
      } else if (aptStart < currentGroupEnd) {
        // Overlaps with current group
        currentGroup.push(apt);
        // Extend group end if this appointment ends later
        if (aptEnd > currentGroupEnd) {
          currentGroupEnd = aptEnd;
        }
      } else {
        // No overlap, start new group
        if (currentGroup.length > 0) {
          groups.push([...currentGroup]);
        }
        currentGroup = [apt];
        currentGroupEnd = aptEnd;
      }
    }
    
    // Push final group
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }
    
    return groups;
  }, []);
  
  // Helper to add minutes to a time string
  const addMinutesToTime = (timeStr, minutes) => {
    const [h, m] = timeStr.split(":").map(Number);
    const totalMins = h * 60 + m + minutes;
    const newH = Math.floor(totalMins / 60) % 24;
    const newM = totalMins % 60;
    return `${newH.toString().padStart(2, "0")}:${newM.toString().padStart(2, "0")}`;
  };
  
  const renderTimeGridEvent = (appointment, overlapIndex = 0, totalOverlaps = 1, dayIndex = 0, totalDays = 1) => {
    const slotIndex = getSlotIndex(appointment.appointment_time);
    if (slotIndex < 0) return null;
    
    const StatusIcon = STATUS_CONFIG[appointment.status]?.icon || CalendarClock;
    const overdue = isOverdue(appointment);
    const timedBg = STATUS_CONFIG[appointment.status]?.timedBg || "bg-blue-500";
    const isDoubleBooked = appointment.allow_double_book || appointment.conflict_group_id;
    
    // Calculate height (default 1 slot = 30 mins)
    const endSlot = appointment.end_time ? getSlotIndex(appointment.end_time) : slotIndex + 1;
    const slots = Math.max(1, endSlot - slotIndex);
    const height = slots * SLOT_HEIGHT - 2;
    const top = slotIndex * SLOT_HEIGHT + 1;
    
    // Calculate width and left offset for overlapping appointments
    const widthPercent = totalOverlaps > 1 ? (100 / totalOverlaps) - 1 : 100;
    const leftPercent = overlapIndex * (100 / totalOverlaps);
    
    // Build summary info
    const visitType = appointment.visit_type || "Visit";
    const addressPreview = truncateAddress(appointment.doctor_address_snapshot || appointment.area || "", 25);
    
    return (
      <motion.div
        key={appointment.appointment_id}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          "absolute rounded-md cursor-pointer transition-all hover:shadow-lg hover:z-20 overflow-hidden",
          timedBg,
          "text-white shadow-sm",
          overdue && "ring-2 ring-red-400",
          isDoubleBooked && "ring-2 ring-amber-400"
        )}
        style={{ 
          top: `${top}px`, 
          height: `${height}px`,
          left: totalOverlaps > 1 ? `calc(${leftPercent}% + 2px)` : "4px",
          width: totalOverlaps > 1 ? `calc(${widthPercent}% - 2px)` : "calc(100% - 8px)",
          zIndex: 10 + overlapIndex
        }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedAppointment(appointment);
          setDetailSheetOpen(true);
        }}
        data-testid={`timed-appointment-${appointment.appointment_id}`}
      >
        <div className="p-1.5 h-full flex flex-col">
          {/* Line 1: Doctor Name + Status Icon */}
          <div className="flex items-start justify-between gap-1">
            <p className="text-xs font-medium truncate flex-1">
              {isDoubleBooked && <span className="mr-0.5" title="Double-booked">⚠️</span>}
              {appointment.doctor_name}
            </p>
            <StatusIcon className="w-3 h-3 flex-shrink-0 opacity-80" />
          </div>
          {/* Line 2: Visit Type • Time Range • Address Preview */}
          <p className="text-[10px] text-white/85 truncate">
            <span className="font-medium">{visitType}</span>
            <span className="mx-0.5">•</span>
            <span>{formatTimeRange(appointment.appointment_time, appointment.end_time)}</span>
            {addressPreview && slots > 1 && totalOverlaps === 1 && (
              <>
                <span className="mx-0.5">•</span>
                <span className="opacity-80">{addressPreview}</span>
              </>
            )}
          </p>
          {/* Show address in larger slots */}
          {slots >= 2 && addressPreview && totalOverlaps === 1 && (
            <p className="text-[9px] text-white/70 truncate mt-auto">
              <MapPin className="w-2.5 h-2.5 inline mr-0.5" />
              {addressPreview}
            </p>
          )}
        </div>
      </motion.div>
    );
  };
  
  // Render all appointments for a day with overlap handling
  const renderDayAppointments = (timedAppointments, dayIndex = 0, totalDays = 1) => {
    const groups = getOverlappingGroups(timedAppointments);
    const rendered = [];
    
    for (const group of groups) {
      const totalInGroup = group.length;
      group.forEach((apt, idx) => {
        rendered.push(renderTimeGridEvent(apt, idx, totalInGroup, dayIndex, totalDays));
      });
    }
    
    return rendered;
  };

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="rep-calendar-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <CalendarDays className="inline-block w-6 h-6 mr-2 text-primary" />
            Rep Calendar
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {isManagerOrAdmin ? "View and manage all rep appointments" : "Your scheduled appointments"}
          </p>
        </div>

        <Dialog open={planDialogOpen} onOpenChange={(open) => {
          if (!open) resetPlanForm();
          setPlanDialogOpen(open);
        }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="plan-appointment-button">
              <Plus className="w-4 h-4" />
              Plan Appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-primary" />
                {editingAppointment ? "Edit Appointment" : "Plan Appointment"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Rep Selection - Required for all users */}
              <div>
                <Label className="text-sm">Assign to Rep *</Label>
                <Select 
                  value={planForm.assigned_rep} 
                  onValueChange={(v) => setPlanForm(p => ({ ...p, assigned_rep: v }))}
                  disabled={user?.role === "rep"}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select rep..." />
                  </SelectTrigger>
                  <SelectContent>
                    {REP_DIRECTORY.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {user?.role === "rep" && (
                  <p className="text-[10px] text-muted-foreground mt-1">Auto-assigned to your rep</p>
                )}
              </div>

              {/* Doctor Type Selector */}
              <div>
                <Label className="text-sm mb-2 block">Doctor</Label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="gap-2"
                    data-testid="doctor-mode-existing"
                  >
                    <User className="w-4 h-4" />
                    Existing
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleNewDoctorRedirect}
                    className="gap-2"
                    data-testid="doctor-mode-new"
                  >
                    <ExternalLink className="w-4 h-4" />
                    New Doctor
                  </Button>
                </div>
              </div>

              {/* Doctor Search (always shown now - existing doctors only) */}
              <div>
                <Label className="text-sm">Select Doctor *</Label>
                <Popover open={doctorSearchOpen} onOpenChange={setDoctorSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "w-full justify-between h-10",
                        !selectedPlanDoctor && "text-muted-foreground"
                      )}
                      data-testid="plan-doctor-select"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <Stethoscope className="w-4 h-4" />
                        <span className="truncate">
                          {selectedPlanDoctor ? selectedPlanDoctor.doctor_name : "Search doctor..."}
                        </span>
                      </div>
                      <ChevronsUpDown className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search doctors..."
                        value={doctorSearchQuery}
                        onValueChange={setDoctorSearchQuery}
                      />
                      <CommandList className="max-h-[200px]">
                        {searchingDoctors ? (
                          <div className="py-4 text-center text-sm text-muted-foreground">Searching...</div>
                        ) : doctorSearchResults.length === 0 ? (
                          <CommandEmpty>No doctors found</CommandEmpty>
                        ) : (
                          <CommandGroup>
                            {doctorSearchResults.map((doc) => (
                              <CommandItem
                                key={doc.doctor_id}
                                onSelect={() => handlePlanDoctorSelect(doc)}
                                className="cursor-pointer"
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedPlanDoctor?.doctor_id === doc.doctor_id ? "opacity-100" : "opacity-0")} />
                                <div>
                                  <p className="text-sm font-medium">{doc.doctor_name}</p>
                                  <p className="text-xs text-muted-foreground">{doc.practice_name}</p>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Doctor Info Section - Show after selecting doctor */}
              {selectedPlanDoctor && (
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="doctor-info-section">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    <Building className="w-3 h-3 inline mr-1" />
                    Doctor Info
                  </p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-start gap-2">
                      <Stethoscope className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium">{selectedPlanDoctor.doctor_name}</span>
                    </div>
                    {selectedPlanDoctor.practice_name && (
                      <div className="flex items-start gap-2">
                        <Building className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <span>{selectedPlanDoctor.practice_name}</span>
                      </div>
                    )}
                    {planForm.area && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <span>{planForm.area}</span>
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" />
                      <span className={cn(
                        "text-xs",
                        selectedPlanDoctor.address ? "text-foreground" : "text-muted-foreground italic"
                      )}>
                        {selectedPlanDoctor.address || planForm.doctor_address_snapshot || "Address not captured"}
                      </span>
                    </div>
                    {(selectedPlanDoctor.doctor_phone || planForm.doctor_phone_snapshot) && (
                      <div className="flex items-start gap-2">
                        <User className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs">{selectedPlanDoctor.doctor_phone || planForm.doctor_phone_snapshot}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Area */}
              <div>
                <Label className="text-sm">Area *</Label>
                <Select value={planForm.area} onValueChange={(v) => setPlanForm(p => ({ ...p, area: v }))}>
                  <SelectTrigger className="h-10" data-testid="plan-area-select">
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    {AREAS.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div>
                <Label className="text-sm">Date *</Label>
                <Input
                  type="date"
                  value={planForm.appointment_date}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setPlanForm(p => ({ ...p, appointment_date: newDate }));
                    if (newDate && planForm.appointment_time) {
                      checkAppointmentConflicts(newDate, planForm.appointment_time, planForm.assigned_rep || null);
                    } else { setConflictWarning(null); }
                  }}
                  className="h-10"
                  data-testid="plan-date-input"
                />
              </div>

              {/* Time (Optional) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Start Time</Label>
                  <Input
                    type="time"
                    value={planForm.appointment_time}
                    onChange={(e) => {
                      const startTime = e.target.value;
                      setPlanForm(p => ({
                        ...p,
                        appointment_time: startTime,
                        end_time: startTime && !p.end_time 
                          ? format(addMinutes(parseISO(`2000-01-01T${startTime}`), 30), "HH:mm")
                          : p.end_time
                      }));
                      if (planForm.appointment_date && startTime) {
                        checkAppointmentConflicts(planForm.appointment_date, startTime, planForm.assigned_rep || null);
                      } else { setConflictWarning(null); }
                    }}
                    className="h-10"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Timed = fixed commitment
                  </p>
                  {conflictChecking && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" />
                      Checking calendar...
                    </p>
                  )}
                  {conflictWarning && conflictWarning.length > 0 && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-amber-800">
                            Schedule conflict — {conflictWarning.length} appointment{conflictWarning.length > 1 ? "s" : ""} within 1 hour
                          </p>
                          <div className="mt-1 space-y-1">
                            {conflictWarning.map((c, i) => (
                              <div key={i} className="text-[11px] text-amber-700 bg-amber-100 rounded p-1.5">
                                <span className="font-medium">{c.doctor_name}</span> · {c.area} · {c.appointment_time} · {c.visit_type}
                              </div>
                            ))}
                          </div>
                          <p className="text-[10px] text-amber-600 mt-1">You can still save — this is a warning only.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm">End Time</Label>
                  <Input
                    type="time"
                    value={planForm.end_time}
                    onChange={(e) => setPlanForm(p => ({ ...p, end_time: e.target.value }))}
                    className="h-10"
                    disabled={!planForm.appointment_time}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Default: 30 mins
                  </p>
                </div>
              </div>
              
              {/* Clear time option */}
              {planForm.appointment_time && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground h-7"
                  onClick={() => setPlanForm(p => ({ ...p, appointment_time: "", end_time: "" }))}
                >
                  <XCircle className="w-3 h-3 mr-1" />
                  Clear time (make unscheduled)
                </Button>
              )}

              {/* Visit Type */}
              <div>
                <Label className="text-sm">Visit Type</Label>
                <Select value={planForm.visit_type} onValueChange={(v) => setPlanForm(p => ({ ...p, visit_type: v }))}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VISIT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm">Notes</Label>
                <Textarea
                  value={planForm.notes}
                  onChange={(e) => setPlanForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Add any notes..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => { setPlanDialogOpen(false); resetPlanForm(); }}>
                Cancel
              </Button>
              <Button onClick={() => editingAppointment ? handleUpdateAppointment() : handleCreateAppointment()} disabled={submitting}>
                {submitting ? "Saving..." : editingAppointment ? "Update Appointment" : "Create Appointment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Filters */}
      <Card className="border-dashed">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filters</span>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 text-xs">
                Clear
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            {/* Area */}
            <Select value={filterArea} onValueChange={setFilterArea}>
              <SelectTrigger className="h-9 text-xs">
                <MapPin className="w-3 h-3 mr-1" />
                <SelectValue placeholder="Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {AREAS.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Rep (Manager only) */}
            {isManagerOrAdmin && (
              <Select value={filterRep} onValueChange={setFilterRep}>
                <SelectTrigger className="h-9 text-xs">
                  <User className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="Rep" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {REP_DIRECTORY.map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Status */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Doctor Search */}
            <Popover open={filterDoctorSearchOpen} onOpenChange={setFilterDoctorSearchOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 text-xs justify-between">
                  <div className="flex items-center gap-1 truncate">
                    <Stethoscope className="w-3 h-3" />
                    <span className="truncate">{filterDoctor ? filterDoctor.doctor_name : "Doctor"}</span>
                  </div>
                  {filterDoctor ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0"
                      onClick={(e) => { e.stopPropagation(); setFilterDoctor(null); setFilterDoctorId(""); }}
                    >
                      <XCircle className="w-3 h-3" />
                    </Button>
                  ) : (
                    <ChevronsUpDown className="w-3 h-3" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search..."
                    value={filterDoctorSearchQuery}
                    onValueChange={setFilterDoctorSearchQuery}
                  />
                  <CommandList className="max-h-[150px]">
                    {filterDoctorSearchResults.length === 0 ? (
                      <CommandEmpty>No doctors</CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {filterDoctorSearchResults.map((doc) => (
                          <CommandItem key={doc.doctor_id} onSelect={() => handleFilterDoctorSelect(doc)}>
                            <Check className={cn("mr-2 h-3 w-3", filterDoctorId === doc.doctor_id ? "opacity-100" : "opacity-0")} />
                            <span className="text-xs">{doc.doctor_name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Navigation */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === "day" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("day")}
                className="h-8 px-3 text-xs"
                data-testid="view-day-button"
              >
                Day
              </Button>
              <Button
                variant={viewMode === "week" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("week")}
                className="h-8 px-3 text-xs"
                data-testid="view-week-button"
              >
                Week
              </Button>
            </div>

            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goPrevious} className="h-8 w-8 p-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday} className="h-8 px-3 text-xs">
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={goNext} className="h-8 w-8 p-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Current Date Display */}
            <div className="text-sm font-medium">
              {viewMode === "day" 
                ? format(currentDate, "EEEE, MMMM d, yyyy")
                : `${format(weekDays[0], "MMM d")} - ${format(weekDays[6], "MMM d, yyyy")}`
              }
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : viewMode === "day" ? (
        /* Day View - Time Grid */
        <Card className="overflow-hidden">
          <CardHeader className="pb-2 border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-primary" />
                {format(currentDate, "EEEE, MMMM d")}
                <Badge variant="secondary" className="text-xs">
                  {getAppointmentsForDate(currentDate).length} appointments
                </Badge>
              </CardTitle>
              {/* Legend */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-blue-500"></div>
                  <span>Timed (fixed)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-blue-200 border border-dashed border-blue-400"></div>
                  <span>Unscheduled</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Unscheduled/All-day Section */}
            {(() => {
              const { untimed } = getAppointmentsByType(currentDate);
              return untimed.length > 0 && (
                <Collapsible open={unscheduledExpanded} onOpenChange={setUnscheduledExpanded}>
                  <CollapsibleTrigger className="w-full p-3 bg-slate-50 border-b flex items-center justify-between hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Unscheduled / Flexible</span>
                      <Badge variant="outline" className="text-xs">{untimed.length}</Badge>
                    </div>
                    {unscheduledExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-3 bg-slate-50/50 border-b">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {untimed.map(apt => renderAppointmentCard(apt, false, false))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })()}
            
            {/* Time Grid */}
            <div className="relative" style={{ height: `${TIME_SLOTS.length * SLOT_HEIGHT}px` }}>
              {/* Time Labels */}
              <div className="absolute left-0 top-0 w-16 h-full border-r bg-muted/20">
                {TIME_SLOTS.map((time, idx) => (
                  <div
                    key={time}
                    className="absolute left-0 w-full px-2 text-xs text-muted-foreground"
                    style={{ top: `${idx * SLOT_HEIGHT}px`, height: `${SLOT_HEIGHT}px` }}
                  >
                    {time.endsWith(':00') && (
                      <span className="font-medium">{formatTimeDisplay(time)}</span>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Grid Lines and Events */}
              <div className="absolute left-16 right-0 top-0 h-full">
                {/* Grid Lines */}
                {TIME_SLOTS.map((time, idx) => (
                  <div
                    key={`line-${time}`}
                    className={cn(
                      "absolute left-0 right-0 border-t cursor-pointer hover:bg-primary/5 transition-colors",
                      time.endsWith(':00') ? "border-slate-200" : "border-slate-100"
                    )}
                    style={{ top: `${idx * SLOT_HEIGHT}px`, height: `${SLOT_HEIGHT}px` }}
                    onClick={() => handleSlotClick(currentDate, time)}
                    data-testid={`slot-${time}`}
                  />
                ))}
                
                {/* Timed Events */}
                <div className="absolute inset-0 pointer-events-none">
                  {(() => {
                    const { timed } = getAppointmentsByType(currentDate);
                    return renderDayAppointments(timed).map((element, idx) => (
                      <div key={idx} className="pointer-events-auto">
                        {element}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Week View - Time Grid */
        <Card className="overflow-hidden">
          <CardHeader className="pb-2 border-b bg-muted/30">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-primary" />
                {`${format(weekDays[0], "MMM d")} - ${format(weekDays[6], "MMM d, yyyy")}`}
              </CardTitle>
              {/* Legend */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-blue-500"></div>
                  <span>Timed (fixed)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-blue-200 border border-dashed border-blue-400"></div>
                  <span>Unscheduled</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Unscheduled/All-day Row */}
            {(() => {
              const totalUnscheduled = weekDays.reduce((sum, day) => sum + getAppointmentsByType(day).untimed.length, 0);
              const hasUnscheduled = totalUnscheduled > 0;
              return hasUnscheduled && (
                <Collapsible open={unscheduledExpanded} onOpenChange={setUnscheduledExpanded}>
                  <CollapsibleTrigger className="w-full p-2 bg-slate-50 border-b flex items-center justify-between hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Unscheduled / Flexible</span>
                      <Badge variant="secondary" className="text-[10px] h-5">{totalUnscheduled}</Badge>
                    </div>
                    {unscheduledExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="grid grid-cols-8 border-b min-h-[80px]">
                      {/* Empty cell for time column */}
                      <div className="w-16 min-w-[64px] bg-muted/20 border-r p-1">
                        <span className="text-[9px] text-muted-foreground">All-day</span>
                      </div>
                      {/* Day columns */}
                      {weekDays.map((day, idx) => {
                        const { untimed } = getAppointmentsByType(day);
                        const isToday = isSameDay(day, new Date());
                        const MAX_VISIBLE = 2;
                        const showMore = untimed.length > MAX_VISIBLE;
                        const dateKey = format(day, "yyyy-MM-dd");
                        
                        return (
                          <div
                            key={idx}
                            className={cn(
                              "p-1.5 min-h-[80px] border-r last:border-r-0 bg-slate-50/50 overflow-y-auto",
                              isToday && "bg-primary/5"
                            )}
                          >
                            {untimed.length === 0 ? (
                              <p className="text-[10px] text-muted-foreground text-center py-2">-</p>
                            ) : (
                              <div className="space-y-1">
                                {untimed.slice(0, MAX_VISIBLE).map(apt => (
                                  <div
                                    key={apt.appointment_id}
                                    className={cn(
                                      "text-[10px] p-1.5 rounded cursor-pointer",
                                      STATUS_CONFIG[apt.status]?.untimedBg || "bg-blue-200",
                                      "hover:opacity-80 transition-opacity border border-dashed"
                                    )}
                                    onClick={() => {
                                      setSelectedAppointment(apt);
                                      setDetailSheetOpen(true);
                                    }}
                                    data-testid={`unscheduled-${apt.appointment_id}`}
                                  >
                                    <p className="font-medium truncate">{apt.doctor_name}</p>
                                    <p className="text-[9px] opacity-70 truncate">{apt.visit_type || "Visit"}</p>
                                  </div>
                                ))}
                                {showMore && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        className="text-[10px] text-primary hover:underline w-full text-left font-medium p-1 bg-primary/10 rounded"
                                        data-testid={`expand-unscheduled-${dateKey}`}
                                      >
                                        +{untimed.length - MAX_VISIBLE} more
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-64 p-2" align="start">
                                      <div className="space-y-1.5">
                                        <p className="text-xs font-medium text-muted-foreground border-b pb-1 mb-2">
                                          Unscheduled ({untimed.length}) - {format(day, "MMM d")}
                                        </p>
                                        <ScrollArea className="max-h-[200px]">
                                          <div className="space-y-1.5 pr-2">
                                            {untimed.map(apt => (
                                              <div
                                                key={apt.appointment_id}
                                                className={cn(
                                                  "text-xs p-2 rounded cursor-pointer",
                                                  STATUS_CONFIG[apt.status]?.untimedBg || "bg-blue-200",
                                                  "hover:opacity-80 transition-opacity"
                                                )}
                                                onClick={() => {
                                                  setSelectedAppointment(apt);
                                                  setDetailSheetOpen(true);
                                                }}
                                              >
                                                <p className="font-medium">{apt.doctor_name}</p>
                                                <p className="text-[10px] opacity-70">{apt.visit_type || "Visit"} • {apt.area || "No area"}</p>
                                              </div>
                                            ))}
                                          </div>
                                        </ScrollArea>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })()}
            
            {/* Day Headers (Sticky) */}
            <div className="grid grid-cols-8 border-b sticky top-0 z-20 bg-white">
              {/* Empty cell for time column */}
              <div className="w-16 min-w-[64px] bg-muted/30 border-r"></div>
              {/* Day headers */}
              {weekDays.map((day, idx) => {
                const isToday = isSameDay(day, new Date());
                const dayAppointments = getAppointmentsForDate(day);
                
                return (
                  <div
                    key={idx}
                    className={cn(
                      "p-2 text-center border-r last:border-r-0",
                      isToday && "bg-primary/10"
                    )}
                  >
                    <p className="text-[10px] text-muted-foreground uppercase font-medium">{format(day, "EEE")}</p>
                    <p className={cn(
                      "text-lg font-bold",
                      isToday && "text-primary"
                    )}>{format(day, "d")}</p>
                    {dayAppointments.length > 0 && (
                      <Badge variant="secondary" className="text-[9px] h-4 px-1">{dayAppointments.length}</Badge>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Time Grid */}
            <ScrollArea className="h-[600px]">
              <div className="relative" style={{ height: `${TIME_SLOTS.length * SLOT_HEIGHT}px` }}>
                <div className="grid grid-cols-8">
                  {/* Time Labels Column */}
                  <div className="w-16 min-w-[64px] border-r bg-muted/10 relative">
                    {TIME_SLOTS.map((time, idx) => (
                      <div
                        key={time}
                        className="absolute left-0 w-full px-1.5 text-[10px] text-muted-foreground"
                        style={{ top: `${idx * SLOT_HEIGHT}px`, height: `${SLOT_HEIGHT}px` }}
                      >
                        {time.endsWith(':00') && (
                          <span className="font-medium">{time}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Day Columns with Time Slots */}
                  {weekDays.map((day, dayIdx) => {
                    const { timed } = getAppointmentsByType(day);
                    const isToday = isSameDay(day, new Date());
                    
                    return (
                      <div
                        key={dayIdx}
                        className={cn(
                          "relative border-r last:border-r-0",
                          isToday && "bg-primary/5"
                        )}
                        style={{ height: `${TIME_SLOTS.length * SLOT_HEIGHT}px` }}
                      >
                        {/* Grid Lines (clickable slots) */}
                        {TIME_SLOTS.map((time, idx) => (
                          <div
                            key={`${dayIdx}-${time}`}
                            className={cn(
                              "absolute left-0 right-0 border-t cursor-pointer hover:bg-primary/10 transition-colors",
                              time.endsWith(':00') ? "border-slate-200" : "border-slate-100/50"
                            )}
                            style={{ top: `${idx * SLOT_HEIGHT}px`, height: `${SLOT_HEIGHT}px` }}
                            onClick={() => handleSlotClick(day, time)}
                          />
                        ))}
                        
                        {/* Timed Events */}
                        {renderDayAppointments(timed, dayIdx, 7)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Appointment Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={(open) => {
        setDetailSheetOpen(open);
        if (!open) setShowReschedulePanel(false);
      }}>
        <SheetContent className="sm:max-w-md flex flex-col max-h-[85vh]">
          {selectedAppointment && (
            <>
              <SheetHeader className="flex-shrink-0">
                <SheetTitle className="flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-primary" />
                  Appointment Details
                </SheetTitle>
              </SheetHeader>
              
              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto mt-4 pr-1">
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge className={cn("text-xs", STATUS_CONFIG[selectedAppointment.status]?.color)}>
                      {selectedAppointment.status}
                    </Badge>
                  </div>

                  {isOverdue(selectedAppointment) && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm text-red-700 font-medium">Overdue</span>
                    </div>
                  )}

                  {/* Doctor Info */}
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{selectedAppointment.doctor_name}</span>
                    </div>
                    {selectedAppointment.practice_name && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building className="w-4 h-4" />
                        <span>{selectedAppointment.practice_name}</span>
                      </div>
                    )}
                    {selectedAppointment.area && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span>{selectedAppointment.area}</span>
                      </div>
                    )}
                    {/* Full Address from snapshot */}
                    {(selectedAppointment.doctor_address_snapshot) && (
                      <div className="flex items-start gap-2 text-sm bg-slate-50 p-2 rounded-lg">
                        <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Address</p>
                          <span className="text-foreground">{selectedAppointment.doctor_address_snapshot}</span>
                        </div>
                      </div>
                    )}
                    {/* Phone from snapshot */}
                    {selectedAppointment.doctor_phone_snapshot && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>{selectedAppointment.doctor_phone_snapshot}</span>
                      </div>
                    )}
                  </div>

                  {/* Date & Time */}
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">
                      {format(parseISO(selectedAppointment.appointment_date), "EEEE, MMMM d, yyyy")}
                      {selectedAppointment.appointment_time && ` at ${formatTimeRange(selectedAppointment.appointment_time, selectedAppointment.end_time)}`}
                    </span>
                  </div>

                  {/* Visit Type */}
                  {selectedAppointment.visit_type && (
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedAppointment.visit_type}</span>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedAppointment.notes && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Notes</p>
                      <p className="text-sm">{selectedAppointment.notes}</p>
                    </div>
                  )}
                  
                  {/* No Show Reason Display */}
                  {selectedAppointment.status === "No Show" && selectedAppointment.no_show_reason && (
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600 mb-1 flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        No Show Reason
                      </p>
                      <p className="text-sm text-red-800">{selectedAppointment.no_show_reason}</p>
                      {selectedAppointment.status_updated_at && (
                        <p className="text-[10px] text-red-500 mt-1">
                          Marked on {format(parseISO(selectedAppointment.status_updated_at), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Last Reschedule Reason Display */}
                  {selectedAppointment.status === "Rescheduled" && selectedAppointment.reschedule_history?.length > 0 && (
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-xs text-orange-600 mb-1 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        Rescheduled
                      </p>
                      <p className="text-sm text-orange-800">
                        {selectedAppointment.reschedule_history[selectedAppointment.reschedule_history.length - 1]?.reason}
                      </p>
                      <p className="text-[10px] text-orange-500 mt-1">
                        From {selectedAppointment.reschedule_history[selectedAppointment.reschedule_history.length - 1]?.original_date} → {selectedAppointment.reschedule_history[selectedAppointment.reschedule_history.length - 1]?.new_date}
                      </p>
                    </div>
                  )}

                  {/* Rep Info (for managers) */}
                  {isManagerOrAdmin && selectedAppointment.rep_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>Assigned to: {getRepDisplayName(selectedAppointment.rep_name)}</span>
                    </div>
                  )}

                  {/* Actions Section */}
                  {selectedAppointment.status === "Planned" && (
                    <div className="border-t pt-4 mt-4 space-y-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
                      
                      {/* Edit Button */}
                      <Button
                        variant="outline"
                        size="default"
                        className="w-full gap-2 h-10"
                        onClick={() => handleEditAppointment(selectedAppointment)}
                        data-testid="btn-edit-appointment"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Edit Appointment</span>
                      </Button>
                      
                      {/* Add time option if unscheduled */}
                      {!selectedAppointment.appointment_time && (
                        <Button
                          variant="secondary"
                          size="default"
                          className="w-full gap-2 h-9 text-sm"
                          onClick={() => {
                            handleEditAppointment(selectedAppointment);
                            // Will open edit dialog where user can add time
                          }}
                          data-testid="btn-add-time"
                        >
                          <Clock className="w-4 h-4" />
                          <span>Add Time (make fixed)</span>
                        </Button>
                      )}
                      
                      {/* Status Action Buttons - Responsive Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Button
                          variant="default"
                          size="default"
                          className="gap-2 bg-green-600 hover:bg-green-700 h-10"
                          onClick={() => handleUpdateStatus(selectedAppointment.appointment_id, "Completed")}
                          disabled={updatingStatus || showReschedulePanel || showNoShowModal}
                          data-testid="btn-mark-completed"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Done</span>
                        </Button>
                        <Button
                          variant="destructive"
                          size="default"
                          className="gap-2 h-10"
                          onClick={() => setShowNoShowModal(true)}
                          disabled={updatingStatus || showReschedulePanel || showNoShowModal}
                          data-testid="btn-mark-noshow"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>No Show</span>
                        </Button>
                        <Button
                          variant={showReschedulePanel ? "secondary" : "outline"}
                          size="default"
                          className="gap-2 h-10"
                          onClick={() => {
                            setShowReschedulePanel(!showReschedulePanel);
                            if (!showReschedulePanel) {
                              setRescheduleDate("");
                              setRescheduleReason("");
                            }
                          }}
                          disabled={updatingStatus}
                          data-testid="btn-reschedule"
                        >
                          <RefreshCw className="w-4 h-4" />
                          <span>Reschedule</span>
                        </Button>
                      </div>

                      {/* Inline Reschedule Panel - Enhanced with dropdown + other */}
                      {showReschedulePanel && (
                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-orange-800">
                            <RefreshCw className="w-4 h-4" />
                            Reschedule Appointment
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <Label className="text-sm text-orange-900">New Date *</Label>
                              <Input
                                type="date"
                                value={rescheduleDate}
                                onChange={(e) => setRescheduleDate(e.target.value)}
                                min={format(new Date(), "yyyy-MM-dd")}
                                className="h-10 mt-1 bg-white"
                                data-testid="reschedule-date-input"
                              />
                            </div>
                            <div>
                              <Label className="text-sm text-orange-900">Reason *</Label>
                              <Select value={rescheduleReason} onValueChange={setRescheduleReason}>
                                <SelectTrigger className="h-10 mt-1 bg-white">
                                  <SelectValue placeholder="Select reason..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {RESCHEDULE_REASONS.map(reason => (
                                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {rescheduleReason === "Other" && (
                              <div>
                                <Label className="text-sm text-orange-900">Specify reason *</Label>
                                <Textarea
                                  value={rescheduleOtherReason}
                                  onChange={(e) => setRescheduleOtherReason(e.target.value)}
                                  placeholder="Please specify the reason..."
                                  rows={2}
                                  className="mt-1 bg-white"
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                setShowReschedulePanel(false);
                                setRescheduleReason("");
                                setRescheduleOtherReason("");
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1 bg-orange-600 hover:bg-orange-700"
                              onClick={handleRescheduleWithReason}
                              disabled={updatingStatus || !rescheduleDate || !rescheduleReason || (rescheduleReason === "Other" && !rescheduleOtherReason.trim())}
                            >
                              {updatingStatus ? "Saving..." : "Confirm Reschedule"}
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* No Show Modal Panel */}
                      {showNoShowModal && (
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-red-800">
                            <XCircle className="w-4 h-4" />
                            Mark as No Show
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <Label className="text-sm text-red-900">Reason *</Label>
                              <Select value={noShowReason} onValueChange={setNoShowReason}>
                                <SelectTrigger className="h-10 mt-1 bg-white">
                                  <SelectValue placeholder="Select reason..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {NO_SHOW_REASONS.map(reason => (
                                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {noShowReason === "Other" && (
                              <div>
                                <Label className="text-sm text-red-900">Specify reason *</Label>
                                <Textarea
                                  value={noShowOtherReason}
                                  onChange={(e) => setNoShowOtherReason(e.target.value)}
                                  placeholder="Please specify the reason..."
                                  rows={2}
                                  className="mt-1 bg-white"
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                setShowNoShowModal(false);
                                setNoShowReason("");
                                setNoShowOtherReason("");
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="flex-1"
                              onClick={handleNoShowWithReason}
                              disabled={updatingStatus || !noShowReason || (noShowReason === "Other" && !noShowOtherReason.trim())}
                            >
                              {updatingStatus ? "Saving..." : "Confirm No Show"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reschedule History - Collapsible */}
                  {selectedAppointment.reschedule_history && selectedAppointment.reschedule_history.length > 0 && (
                    <Collapsible className="border-t pt-4 mt-4">
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4" />
                          <span>Reschedule History ({selectedAppointment.reschedule_history.length})</span>
                        </div>
                        <ChevronRight className="w-4 h-4 transition-transform duration-200 group-data-[state=open]:rotate-90" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-2">
                        <div className="space-y-3 pl-6">
                          {selectedAppointment.reschedule_history.map((entry, idx) => (
                            <div key={idx} className="text-sm border-l-2 border-orange-400 pl-3 py-1">
                              <p className="text-muted-foreground">
                                {entry.original_date} → <span className="font-medium text-foreground">{entry.new_date}</span>
                              </p>
                              <p className="text-orange-700 text-xs mt-0.5">Reason: {entry.reason}</p>
                              {entry.changed_at && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Changed: {new Date(entry.changed_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      
      {/* Reschedule Dialog with Reason */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-600" />
              Reschedule Appointment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">New Date *</Label>
              <Input
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                min={format(new Date(), "yyyy-MM-dd")}
                className="h-10"
                data-testid="reschedule-date-input"
              />
            </div>
            <div>
              <Label className="text-sm">Reason for Rescheduling *</Label>
              <Textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="e.g., Doctor unavailable, Rep schedule conflict..."
                rows={2}
                data-testid="reschedule-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleReschedule(selectedAppointment?.appointment_id, rescheduleDate, rescheduleReason)}
              disabled={updatingStatus || !rescheduleDate || !rescheduleReason}
            >
              {updatingStatus ? "Saving..." : "Confirm Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Visit Prompt Dialog */}
      <Dialog open={logVisitPromptOpen} onOpenChange={setLogVisitPromptOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Appointment Completed
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Would you like to log this as a visit now?
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setLogVisitPromptOpen(false)}>
              No, just mark complete
            </Button>
            <Button
              onClick={() => {
                setLogVisitPromptOpen(false);
                // Navigate to visits page with appointment data pre-filled
                // Include rep_name and area from the appointment
                const params = new URLSearchParams({
                  doctor_id: completedAppointment?.doctor_id || "",
                  appointment_id: completedAppointment?.appointment_id || "",
                  doctor_name: completedAppointment?.doctor_name || "",
                  practice_name: completedAppointment?.practice_name || "",
                  visit_type: completedAppointment?.visit_type || "",
                  rep_name: completedAppointment?.rep_name || "",  // Pass rep from appointment
                  area: completedAppointment?.area || ""
                });
                window.location.href = `/visits?${params.toString()}`;
              }}
              data-testid="btn-log-visit-yes"
            >
              Yes, log visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Conflict Warning Dialog (Google Calendar-style) */}
      <Dialog open={conflictDialogOpen} onOpenChange={(open) => { if (!open) handleCancelDoubleBook(); }}>
        <DialogContent className="max-w-md" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              This time is already booked
            </DialogTitle>
            <DialogDescription>
              The selected time overlaps with existing appointments. You can change the time or proceed with a double-booking.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <p className="text-sm font-medium text-muted-foreground">Conflicting appointments:</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {conflictingAppointments.map((conflict, idx) => (
                <div 
                  key={conflict.appointment_id || idx} 
                  className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">
                      {conflict.appointment_time} - {conflict.end_time || "?"}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-amber-800">
                    <span className="font-medium">{conflict.doctor_name}</span>
                    {conflict.practice_name && (
                      <span className="text-amber-600"> @ {conflict.practice_name}</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-amber-600">
                    {conflict.visit_type} • {conflict.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleCancelDoubleBook}
              className="w-full sm:w-auto"
              data-testid="btn-change-time"
            >
              Change time
            </Button>
            <Button
              variant="default"
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
              onClick={pendingAppointmentData?.isUpdate ? handleConfirmDoubleBookUpdate : handleConfirmDoubleBook}
              disabled={submitting}
              data-testid="btn-double-book"
            >
              {submitting ? "Saving..." : "Double-book anyway"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RepCalendarPage;
